import React from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { MENU, pickLang } from "../../lib/restaurantData";
import { useT } from "../../lib/i18n";

const TAB_KEYS = ["antipasti", "primi", "secondi", "insalate", "pizze", "dolci", "bevande"];

const DishRow = ({ dish, lang }) => {
  const desc = pickLang(dish.desc, lang);
  const note = pickLang(dish.note, lang);
  return (
    <div
      data-testid={`dish-${dish.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
      className="flex justify-between gap-6 py-4 border-b border-[#1E3A5D]/10 last:border-0"
    >
      <div className="min-w-0">
        <h4 className="font-display text-sm sm:text-base text-[#1E3A5D] tracking-[0.14em] font-semibold">
          {dish.name}
        </h4>
        {desc && (
          <p className="mt-1.5 text-[#1E3A5D]/70 text-sm leading-relaxed font-serif-body italic">
            {desc}
          </p>
        )}
        {note && (
          <p className="mt-1 text-[#B59410] text-xs font-display tracking-wider">{note}</p>
        )}
      </div>
      <div className="shrink-0 text-right">
        <span className="font-display text-base sm:text-lg text-[#B59410] font-bold whitespace-nowrap">
          € {dish.price}
        </span>
      </div>
    </div>
  );
};

export const Menu = () => {
  const { t, lang } = useT();
  return (
    <section
      id="menu"
      data-testid="menu-section"
      className="py-20 sm:py-28 bg-[#EDE3D2]"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <span className="font-display text-xs text-[#B59410] tracking-[0.3em] font-semibold">
            {t("menu.eyebrow")}
          </span>
          <h2 className="mt-3 font-script text-6xl sm:text-7xl lg:text-8xl text-[#1E3A5D] leading-none">
            {t("menu.title")}
          </h2>
          <div className="mx-auto mt-4 flex items-center gap-3 justify-center">
            <span className="h-px w-10 bg-[#B59410]" />
            <span className="text-[#B59410] text-xs font-display tracking-[0.3em]">{t("menu.subtitle")}</span>
            <span className="h-px w-10 bg-[#B59410]" />
          </div>
          <p className="mt-6 text-[#1E3A5D]/70 text-base sm:text-lg font-serif-body italic">
            {t("menu.intro")}
          </p>
        </div>

        <Tabs defaultValue="pizze" className="mt-12">
          <div className="overflow-x-auto -mx-4 px-4 sm:overflow-visible sm:px-0 sm:mx-0">
            <TabsList
              data-testid="menu-tabs"
              className="bg-white border border-[#1E3A5D]/15 p-1.5 rounded-full mx-auto inline-flex sm:flex flex-nowrap sm:flex-wrap gap-1 h-auto justify-start sm:justify-center max-w-none"
            >
              {TAB_KEYS.map((k) => (
                <TabsTrigger
                  key={k}
                  value={k}
                  data-testid={`menu-tab-${k}`}
                  className="font-display rounded-full px-5 py-2 text-xs sm:text-sm tracking-[0.18em] data-[state=active]:bg-[#1E3A5D] data-[state=active]:text-white text-[#1E3A5D]/80 whitespace-nowrap"
                >
                  {t(`menu.tabs.${k}`)}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {TAB_KEYS.map((k) => (
            <TabsContent key={k} value={k} className="mt-10">
              <div className="bg-white rounded-3xl border border-[#1E3A5D]/15 shadow-sm p-6 sm:p-10 space-y-12">
                {MENU[k].sections.map((sec) => (
                  <div key={pickLang(sec.title, lang)}>
                    <div className="flex items-center gap-4 mb-6">
                      <span className="h-px flex-1 bg-[#B59410]/40" />
                      <h3 className="font-script text-3xl sm:text-4xl text-[#1E3A5D] text-center">
                        {pickLang(sec.title, lang)}
                      </h3>
                      <span className="h-px flex-1 bg-[#B59410]/40" />
                    </div>
                    <div className="grid md:grid-cols-2 gap-x-12">
                      {sec.items.map((dish) => (
                        <DishRow key={dish.name} dish={dish} lang={lang} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>

        <p className="text-center text-[#1E3A5D]/60 text-xs mt-8 font-serif-body italic">
          {t("menu.footer")}
        </p>
      </div>
    </section>
  );
};
